import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow, subDays } from 'date-fns';
import {
  FileText,
  PlayCircle,
  CheckCircle2,
  Pencil,
  Eye,
  ArrowRightLeft,
  Bell,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { supabase } from '@/services/db/supabase';
import { useAuthStore } from '@/store/authStore';
import { canAmend } from '@/utils/dsr';
import {
  getDSRTrend,
  getSafetyEventTrend as getSafetyEventDailyTrend,
  getSSETrend,
  getTopActionItems,
} from '@/services/db/analytics';
import {
  ChartCard,
  DailyRiskChart,
  SafetyEventLineChart,
  SSEAreaChart,
  TopActionItemsCard,
  useChartState,
} from '@/components/analytics/AnalyticsCharts';

const ANALYTICS_STALE = 5 * 60 * 1000;

const today = () => format(new Date(), 'yyyy-MM-dd');

// ── Status / risk badge styling (matches existing pattern) ──
const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  draft: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Draft' },
  submitted: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Submitted' },
  reviewed: { className: 'bg-mozaic-teal/15 text-mozaic-teal', label: 'Reviewed' },
  flagged: { className: 'bg-mozaic-orange/25 text-mozaic-orange', label: 'Flagged' },
  amended: { className: 'bg-mozaic-blue/15 text-mozaic-blue', label: 'Amended' },
};

const RISK_BADGE: Record<string, { className: string; label: string }> = {
  low: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Low' },
  medium: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Medium' },
  high: { className: 'bg-mozaic-red/15 text-mozaic-red', label: 'High' },
  critical: { className: 'bg-mozaic-red/20 text-mozaic-red font-bold', label: 'Critical' },
};

// ── Queries ─────────────────────────────────────────────────

function useProject() {
  const projectId = useAuthStore((s) => s.projectId);
  return useQuery({
    queryKey: ['ssr_project', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, code')
        .eq('id', projectId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!projectId,
  });
}

interface MyTodayDSR {
  id: string;
  status: string;
  shift_type: string;
  report_date: string;
  submitted_at: string | null;
  created_at: string;
  created_by: string;
}

function useMyTodayDSR() {
  const projectId = useAuthStore((s) => s.projectId);
  const userId = useAuthStore((s) => s.user?.id);

  return useQuery({
    queryKey: ['ssr_my_today_dsr', projectId, userId, today()],
    queryFn: async (): Promise<MyTodayDSR | null> => {
      const { data, error } = await supabase
        .from('dsr_reports')
        .select('id, status, shift_type, report_date, submitted_at, created_at, created_by')
        .eq('project_id', projectId!)
        .eq('created_by', userId!)
        .eq('report_date', today())
        .eq('is_deleted', false)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as MyTodayDSR[];
      if (rows.length === 0) return null;
      // Prefer submitted > amended > flagged > reviewed > draft
      const priority = ['submitted', 'amended', 'reviewed', 'flagged', 'draft'];
      rows.sort(
        (a, b) =>
          priority.indexOf(a.status) - priority.indexOf(b.status)
      );
      return rows[0];
    },
    enabled: !!projectId && !!userId,
  });
}

function useDraftSectionProgress(dsrId: string | null | undefined) {
  return useQuery({
    queryKey: ['ssr_draft_progress', dsrId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dsr_section_selections')
        .select('completion_status, is_included')
        .eq('dsr_report_id', dsrId!)
        .eq('is_deleted', false)
        .eq('is_included', true);
      if (error) throw error;
      const rows = data ?? [];
      const total = rows.length;
      const complete = rows.filter((r) => r.completion_status === 'complete').length;
      return { complete, total };
    },
    enabled: !!dsrId,
  });
}

function useProjectDSRFeed() {
  const projectId = useAuthStore((s) => s.projectId);
  const since = format(subDays(new Date(), 30), 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['ssr_project_dsr_feed', projectId, since],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dsr_reports')
        .select(
          `
          id,
          report_date,
          shift_type,
          status,
          submitted_at,
          created_by,
          users!dsr_reports_created_by_fkey ( first_name, last_name, email ),
          dsr_activities_summaries ( overall_risk_level )
        `
        )
        .eq('project_id', projectId!)
        .eq('is_deleted', false)
        .gte('report_date', since)
        .order('report_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(25);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!projectId,
  });
}

function useLastShiftHandover() {
  const projectId = useAuthStore((s) => s.projectId);
  const userId = useAuthStore((s) => s.user?.id);

  return useQuery({
    queryKey: ['ssr_last_handover', projectId, userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dsr_reports')
        .select(
          `
          id,
          report_date,
          shift_type,
          status,
          submitted_at,
          created_by,
          users!dsr_reports_created_by_fkey ( first_name, last_name ),
          dsr_activities_summaries ( overall_risk_level ),
          dsr_section_selections ( id, is_included )
        `
        )
        .eq('project_id', projectId!)
        .neq('created_by', userId!)
        .eq('status', 'submitted')
        .eq('is_deleted', false)
        .order('report_date', { ascending: false })
        .order('submitted_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      const row = data?.[0] ?? null;
      if (!row) return null;
      const sections = (row.dsr_section_selections as unknown as
        { id: string; is_included: boolean }[]) ?? [];
      const sectionCount = sections.filter((s) => s.is_included).length;
      return { row, sectionCount };
    },
    enabled: !!projectId && !!userId,
  });
}

function useSSRAnalytics(projectId: string | null) {
  const ids = projectId ? [projectId] : [];
  const enabled = !!projectId;
  const dsrTrend = useQuery({
    queryKey: ['ssr_dsr_trend', projectId],
    queryFn: () => getDSRTrend(ids, 30),
    enabled,
    staleTime: ANALYTICS_STALE,
  });
  const safetyTrend = useQuery({
    queryKey: ['ssr_safety_daily', projectId],
    queryFn: () => getSafetyEventDailyTrend(ids, 30),
    enabled,
    staleTime: ANALYTICS_STALE,
  });
  const sseTrend = useQuery({
    queryKey: ['ssr_sse_trend', projectId],
    queryFn: () => getSSETrend(ids, 30),
    enabled,
    staleTime: ANALYTICS_STALE,
  });
  const actions = useQuery({
    queryKey: ['ssr_top_actions', projectId],
    queryFn: () => getTopActionItems(ids, 5),
    enabled,
    staleTime: ANALYTICS_STALE,
  });
  return { dsrTrend, safetyTrend, sseTrend, actions };
}

// ── Sub-components ──────────────────────────────────────────

function NoProjectAssigned() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <FileText className="h-10 w-10 text-muted-foreground/50" />
        <h2 className="text-base font-semibold">No project assigned</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Your account is not yet assigned to a project. Contact your supervisor or tenant
          administrator to be added to a project crew.
        </p>
      </CardContent>
    </Card>
  );
}

interface TodayCardProps {
  loading: boolean;
  dsr: MyTodayDSR | null;
  progress: { complete: number; total: number } | undefined;
  amendable: boolean;
}

function TodayStatusCard({ loading, dsr, progress, amendable }: TodayCardProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card>
        <CardContent className="space-y-3 py-6">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
          <Skeleton className="h-10 w-44" />
        </CardContent>
      </Card>
    );
  }

  // State 3 — submitted
  if (dsr && (dsr.status === 'submitted' || dsr.status === 'reviewed' || dsr.status === 'amended')) {
    const submittedAt = dsr.submitted_at ? new Date(dsr.submitted_at) : null;
    const statusBadge = STATUS_BADGE[dsr.status] ?? STATUS_BADGE.submitted;

    return (
      <Card className="border-mozaic-green/30">
        <CardContent className="flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="h-10 w-10 shrink-0 text-mozaic-green" />
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">Today's report submitted</h2>
                <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {submittedAt
                  ? `Submitted ${format(submittedAt, 'MMM d, yyyy h:mm a')}`
                  : 'Submitted'}
                {' · '}
                <span className="capitalize">{dsr.shift_type}</span> shift
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/reports/${dsr.id}`)}
            >
              <Eye className="mr-1 h-4 w-4" />
              View Report
            </Button>
            {amendable && (
              <Button
                size="sm"
                className="bg-mozaic-blue text-white hover:bg-mozaic-blue/90"
                onClick={() => navigate(`/reports/${dsr.id}/amend`)}
              >
                <Pencil className="mr-1 h-4 w-4" />
                Amend Report
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // State 2 — draft (or flagged-with-no-submitted-at)
  if (dsr && (dsr.status === 'draft' || dsr.status === 'flagged')) {
    const total = progress?.total ?? 0;
    const complete = progress?.complete ?? 0;
    const pct = total > 0 ? Math.round((complete / total) * 100) : 0;
    const elapsed = formatDistanceToNow(new Date(dsr.created_at), { addSuffix: false });

    return (
      <Card className="border-mozaic-orange/40">
        <CardContent className="space-y-4 py-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <PlayCircle className="h-10 w-10 shrink-0 text-mozaic-orange" />
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">Report in progress</h2>
                  <Badge className={STATUS_BADGE.draft.className}>Draft</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {total > 0
                    ? `${complete} of ${total} sections complete`
                    : 'No sections selected yet'}
                  {' · '}
                  Started {elapsed} ago
                  {' · '}
                  <span className="capitalize">{dsr.shift_type}</span> shift
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
              onClick={() => navigate(`/dsr/${dsr.id}/edit`)}
            >
              Continue Report
            </Button>
          </div>
          {/* Progress bar */}
          <div className="space-y-1">
            <div className="h-2 w-full rounded-full bg-muted">
              <div
                className="h-2 rounded-full bg-mozaic-teal transition-[width] duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-right text-xs text-muted-foreground">{pct}% complete</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // State 1 — no DSR yet
  return (
    <Card className="border-mozaic-teal/30">
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <FileText className="h-12 w-12 text-mozaic-teal/60" />
        <h2 className="text-xl font-semibold">No report started for today</h2>
        <p className="text-sm text-muted-foreground">
          {format(new Date(), 'EEEE, MMMM d, yyyy')}
        </p>
        <Button
          size="lg"
          className="mt-2 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
          onClick={() => navigate('/dsr/new')}
        >
          Start Today's Report
        </Button>
      </CardContent>
    </Card>
  );
}

function ProjectDSRFeedCard({ projectName }: { projectName: string }) {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id);
  const userRole = useAuthStore((s) => s.userRole);
  const feed = useProjectDSRFeed();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">All Reports — {projectName}</CardTitle>
        <p className="text-xs text-muted-foreground">
          Showing reports from all SSRs assigned to this project
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Submitted By</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Risk Level</TableHead>
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {feed.isLoading &&
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {feed.isError && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                  Could not load project reports.
                </TableCell>
              </TableRow>
            )}

            {!feed.isLoading && !feed.isError && (feed.data?.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                  No reports for this project in the last 30 days.
                </TableCell>
              </TableRow>
            )}

            {!feed.isLoading &&
              !feed.isError &&
              feed.data?.map((row: any) => {
                const author = row.users as any;
                const summaries = row.dsr_activities_summaries as any[];
                const riskLevel = summaries?.[0]?.overall_risk_level ?? null;
                const statusBadge = STATUS_BADGE[row.status] ?? STATUS_BADGE.draft;
                const riskBadge = riskLevel ? RISK_BADGE[riskLevel] : null;
                const isMine = row.created_by === userId;
                const showEdit =
                  isMine &&
                  canAmend(
                    {
                      status: row.status,
                      submitted_at: row.submitted_at,
                      created_by: row.created_by,
                    },
                    userId ?? '',
                    userRole ?? ''
                  );

                return (
                  <TableRow key={row.id} className="hover:bg-muted/50">
                    <TableCell className="font-medium">
                      {format(new Date(row.report_date), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="capitalize">{row.shift_type}</TableCell>
                    <TableCell>
                      {author ? `${author.first_name} ${author.last_name}` : '—'}
                      {isMine && (
                        <Badge variant="outline" className="ml-2 text-[10px]">
                          You
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
                    </TableCell>
                    <TableCell>
                      {riskBadge ? (
                        <Badge className={riskBadge.className}>{riskBadge.label}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/reports/${row.id}`)}
                        >
                          View
                        </Button>
                        {showEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-mozaic-blue"
                            onClick={() => navigate(`/reports/${row.id}/amend`)}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ProjectAlertsCard() {
  // Alerts module not built yet — empty state placeholder.
  // When the alerts table lands, this becomes a real query scoped to projectId.
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Project Alerts</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3 rounded-md bg-muted/40 px-4 py-6 text-sm text-muted-foreground">
          <Bell className="h-5 w-5" />
          No active alerts for this project.
        </div>
      </CardContent>
    </Card>
  );
}

function HandoverCard() {
  const navigate = useNavigate();
  const handover = useLastShiftHandover();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Last Shift Handover</CardTitle>
      </CardHeader>
      <CardContent>
        {handover.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-9 w-36" />
          </div>
        ) : !handover.data ? (
          <div className="flex items-center gap-3 rounded-md bg-muted/40 px-4 py-6 text-sm text-muted-foreground">
            <ArrowRightLeft className="h-5 w-5" />
            No handover data available.
          </div>
        ) : (
          (() => {
            const { row, sectionCount } = handover.data;
            const author = (row.users as any);
            const summaries = row.dsr_activities_summaries as any[];
            const riskLevel = summaries?.[0]?.overall_risk_level ?? null;
            const riskBadge = riskLevel ? RISK_BADGE[riskLevel] : null;

            return (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <ArrowRightLeft className="h-6 w-6 shrink-0 text-mozaic-teal" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {format(new Date(row.report_date), 'MMM d, yyyy')}
                      {' · '}
                      <span className="capitalize">{row.shift_type}</span> shift
                      {' · '}
                      {author ? `${author.first_name} ${author.last_name}` : 'Unknown SSR'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {sectionCount} {sectionCount === 1 ? 'section' : 'sections'} completed
                    </p>
                    {riskBadge && (
                      <Badge className={riskBadge.className}>{riskBadge.label} risk</Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/reports/${row.id}`)}
                >
                  View Full Report
                </Button>
              </div>
            );
          })()
        )}
      </CardContent>
    </Card>
  );
}

// ── Main ────────────────────────────────────────────────────

export default function SSRDashboard() {
  const projectId = useAuthStore((s) => s.projectId);
  const userId = useAuthStore((s) => s.user?.id);
  const userRole = useAuthStore((s) => s.userRole);

  const project = useProject();
  const todayDsr = useMyTodayDSR();
  const draftId =
    todayDsr.data && (todayDsr.data.status === 'draft' || todayDsr.data.status === 'flagged')
      ? todayDsr.data.id
      : null;
  const progress = useDraftSectionProgress(draftId);
  const navigate = useNavigate();
  const analytics = useSSRAnalytics(projectId ?? null);

  const dsrTrendState = useChartState(analytics.dsrTrend, 'No submitted DSRs in this period');
  const dsrTrendEmpty =
    !analytics.dsrTrend.isLoading && !analytics.dsrTrend.isError &&
    (analytics.dsrTrend.data ?? []).every((p) => p.dsr_count === 0);
  const safetyTrendState = useChartState(analytics.safetyTrend, 'No safety events recorded');
  const safetyTrendEmpty =
    !analytics.safetyTrend.isLoading && !analytics.safetyTrend.isError &&
    (analytics.safetyTrend.data ?? []).every(
      (p) => p.near_miss_count + p.first_aid_count + p.recordable_count + p.lti_count + p.good_catch_count === 0,
    );
  const sseHasData = (analytics.sseTrend.data ?? []).length > 0;

  if (!projectId) {
    return <NoProjectAssigned />;
  }

  const projectName = project.data?.name ?? 'Your Project';
  const projectCode = project.data?.code ? ` (${project.data.code})` : '';

  const amendable = todayDsr.data
    ? canAmend(
        {
          status: todayDsr.data.status,
          submitted_at: todayDsr.data.submitted_at,
          created_by: todayDsr.data.created_by,
        },
        userId ?? '',
        userRole ?? ''
      )
    : false;

  return (
    <div className="space-y-6">
      {/* Scope context bar */}
      <div className="rounded-md bg-mozaic-navy/5 px-4 py-2 text-sm">
        Viewing:{' '}
        <span className="font-medium">
          {projectName}
          {projectCode}
        </span>
        {' — '}
        {format(new Date(), 'EEEE, MMM d, yyyy')}
      </div>

      {/* Row 1 — Today's status */}
      <TodayStatusCard
        loading={todayDsr.isLoading}
        dsr={todayDsr.data ?? null}
        progress={progress.data}
        amendable={amendable}
      />

      {/* Analytics — Row 1 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title="Daily Risk Level — Last 30 Days"
          {...dsrTrendState}
          isEmpty={dsrTrendEmpty}
        >
          <DailyRiskChart data={analytics.dsrTrend.data ?? []} />
        </ChartCard>
        <ChartCard
          title="Safety Events — Last 30 Days"
          {...safetyTrendState}
          isEmpty={safetyTrendEmpty}
        >
          <SafetyEventLineChart data={analytics.safetyTrend.data ?? []} />
        </ChartCard>
      </div>

      {/* Analytics — Row 2 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {sseHasData || analytics.sseTrend.isLoading ? (
          <ChartCard
            title="SSE % — Last 30 Days"
            isLoading={analytics.sseTrend.isLoading}
            isError={analytics.sseTrend.isError}
            isEmpty={!sseHasData && !analytics.sseTrend.isLoading}
            emptyMessage="No POB manifests in this period"
            onRetry={analytics.sseTrend.refetch}
          >
            <SSEAreaChart data={analytics.sseTrend.data ?? []} />
          </ChartCard>
        ) : null}
        <TopActionItemsCard
          items={analytics.actions.data}
          isLoading={analytics.actions.isLoading}
          isError={analytics.actions.isError}
          onViewAll={() => navigate('/reports')}
        />
      </div>

      {/* Row 2 — Project DSR feed */}
      <ProjectDSRFeedCard projectName={projectName} />

      {/* Row 3 — Project alerts */}
      <ProjectAlertsCard />

      {/* Row 4 — Handover */}
      <HandoverCard />
    </div>
  );
}
